<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import ModalWrapper from './ModalWrapper.vue';
import { rpc } from '../../../composables/useRpc.js';

const props = defineProps({
  show: { type: Boolean, default: false },
  phone: { type: String, default: '' },
  conversationId: { type: [Number, String], default: null },
});
const emit = defineEmits(['close', 'created']);

const catalog = ref([]);
const search = ref('');
const selected = ref([]);
const title = ref('');
const loading = ref(false);
const error = ref('');

const filtered = computed(() => {
  const q = search.value.toLowerCase().trim();
  if (!q) return catalog.value;
  return catalog.value.filter(item => item.name.toLowerCase().includes(q));
});

watch(() => props.show, (val) => {
  if (val) {
    loadCatalog();
    selected.value = [];
    title.value = '';
    error.value = '';
  }
});

onMounted(() => {
  if (props.show) loadCatalog();
});

async function loadCatalog() {
  try {
    const data = await rpc('qq-list');
    catalog.value = data || [];
  } catch (e) {
    error.value = 'Error cargando catálogo';
  }
}

function toggle(id) {
  const idx = selected.value.indexOf(id);
  if (idx >= 0) selected.value.splice(idx, 1);
  else selected.value.push(id);
}

async function create() {
  if (!selected.value.length) return;
  loading.value = true;
  error.value = '';
  try {
    await rpc('create-price-list', {
      body: {
        qq_ids: selected.value,
        title: title.value || undefined,
        phone: props.phone,
        conversation_id: props.conversationId,
      },
    });
    emit('created');
    emit('close');
  } catch (e) {
    error.value = e.message || 'Error al crear lista';
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <ModalWrapper :show="show" title="Crear lista de precios" @close="emit('close')">
    <div class="aj-pl-search">
      <input
        v-model="search"
        type="text"
        placeholder="Buscar producto..."
        class="aj-pl-input"
      />
    </div>

    <div class="aj-pl-list">
      <label
        v-for="item in filtered"
        :key="item.id"
        class="aj-pl-item"
      >
        <input
          type="checkbox"
          :checked="selected.includes(item.id)"
          @change="toggle(item.id)"
        />
        <span class="aj-pl-name">{{ item.name }}</span>
      </label>
      <div v-if="!filtered.length" class="aj-pl-empty">
        Sin resultados
      </div>
    </div>

    <div v-if="selected.length" class="aj-pl-title-wrap">
      <input
        v-model="title"
        type="text"
        placeholder="Título (opcional)"
        class="aj-pl-input"
      />
    </div>

    <div v-if="error" class="aj-pl-error">{{ error }}</div>

    <div class="aj-pl-footer">
      <span class="aj-pl-count">{{ selected.length }} seleccionados</span>
      <button
        class="aj-btn aj-btn-primary"
        :disabled="!selected.length || loading"
        @click="create"
      >
        {{ loading ? 'Creando...' : 'Crear lista' }}
      </button>
    </div>
  </ModalWrapper>
</template>

<style scoped>
.aj-pl-search {
  margin-bottom: 10px;
}

.aj-pl-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid rgb(var(--slate-5, 224 224 230));
  border-radius: 8px;
  background: rgb(var(--slate-1, 252 252 253));
  color: rgb(var(--slate-12, 28 32 36));
  font-size: 13px;
  box-sizing: border-box;
}

.aj-pl-input:focus {
  outline: none;
  border-color: rgb(var(--blue-8, 62 99 221));
  box-shadow: 0 0 0 1px rgb(var(--blue-8, 62 99 221));
}

.aj-pl-list {
  max-height: 240px;
  overflow-y: auto;
  margin-bottom: 10px;
  border: 1px solid rgb(var(--slate-4, 232 232 236));
  border-radius: 8px;
}

.aj-pl-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid rgb(var(--slate-3, 240 240 243));
  cursor: pointer;
  min-height: 44px;
  box-sizing: border-box;
}

.aj-pl-item:last-child {
  border-bottom: none;
}

.aj-pl-item:hover {
  background: rgb(var(--slate-2, 248 248 250));
}

.aj-pl-item input[type="checkbox"] {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  cursor: pointer;
}

.aj-pl-name {
  font-size: 13px;
  color: rgb(var(--slate-12, 28 32 36));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.aj-pl-empty {
  padding: 16px;
  text-align: center;
  font-size: 12px;
  color: rgb(var(--slate-9, 142 142 147));
  font-style: italic;
}

.aj-pl-title-wrap {
  margin-bottom: 10px;
}

.aj-pl-error {
  font-size: 12px;
  color: rgb(var(--red-9, 229 72 77));
  margin-bottom: 8px;
}

.aj-pl-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 10px;
  border-top: 1px solid rgb(var(--slate-4, 232 232 236));
}

.aj-pl-count {
  font-size: 12px;
  color: rgb(var(--slate-9, 142 142 147));
}

.aj-btn {
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  min-height: 44px;
}

.aj-btn-primary {
  background: rgb(var(--blue-9, 62 99 221));
  color: #fff;
}

.aj-btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
